// File: src/pages/reveal/RevealPartner.jsx
import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getRevealSocket,
  startRevealAccessWatch,
  stopRevealAccessWatch,
  startLocationHeartbeat,
  stopLocationHeartbeat,
} from "../../utils/revealSocket";
import ChatInput from "../../components/Reveal/RevealChatInput";
import RevealPortalNavBar from "../../components/Reveal/RevealPortalNavBar";
import RevealChatHeader from "../../components/Reveal/RevealChatHeader";
import { enforceRevealState, markFoundPartner } from "../../utils/revealEnforcer";

/* helpers */
function authHeaders() {
  const token =
    localStorage.getItem("accessToken") ||
    JSON.parse(localStorage.getItem("tribe_auth") || "{}")?.token ||
    "";
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}
function getUserId() {
  const ctx = JSON.parse(localStorage.getItem("tribe_auth") || "{}");
  return (
    ctx?.uid ||
    ctx?.userId ||
    ctx?.user?._id ||
    localStorage.getItem("userId") ||
    ""
  );
}
function avatarUrl(u) {
  return u?.profilePicUrl || "/default-avatar.png";
}
function formatName(u) {
  if (!u) return "User";
  const fn = u.firstName || "User";
  const ln = u.lastName ? ` ${u.lastName.charAt(0).toUpperCase()}.` : "";
  return `${fn}${ln}`;
}

/* ───────────────────────── UI bits ───────────────────────── */

// --- iOS keyboard docking (visualViewport) -------------------
function useKeyboardDock() {
  const [offset, setOffset] = React.useState(0); // px keyboard overlap
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    const vv = window.visualViewport;

    const onResize = () => {
      if (!vv) {
        setOffset(0);
        try {
          document.documentElement.style.removeProperty("--kb-offset");
        } catch {}
        return;
      }
      const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      const px = Math.round(overlap);
      setOffset(px);
      // expose as CSS var for layout calc()
      try {
        document.documentElement.style.setProperty("--kb-offset", `${px}px`);
      } catch {}

      // when keyboard opens -> remember scroll & pin body
      if (px > 0 && scrollRef.current == null) {
        scrollRef.current = window.scrollY;
        document.body.classList.add("kbd-open");
      }

      // when keyboard closes -> restore scroll & unpin
      if (px === 0 && scrollRef.current != null) {
        const y = scrollRef.current;
        scrollRef.current = null;
        document.body.classList.remove("kbd-open");
        requestAnimationFrame(() => {
          window.scrollTo({ top: y, behavior: "instant" });
          requestAnimationFrame(() => window.scrollTo({ top: y, behavior: "instant" }));
        });
      }
    };

    onResize();
    vv?.addEventListener("resize", onResize);
    vv?.addEventListener("scroll", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      vv?.removeEventListener("resize", onResize);
      vv?.removeEventListener("scroll", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  return { keyboardOffset: offset };
}

function MessageRow({ isMe, avatar, name, text }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: isMe ? "flex-end" : "flex-start",
        gap: 8,
        marginBottom: 14,
      }}
    >
      {!isMe && (
        <img
          src={avatar}
          alt=""
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
          }}
          onError={(e) => (e.currentTarget.src = "/default-avatar.png")}
        />
      )}

      <div style={{ maxWidth: "min(78%, 560px)", minWidth: 0 }}>
        {!isMe && (
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginBottom: 4,
              fontWeight: 800,
            }}
          >
            {name}
          </div>
        )}
        <div
          style={{
            display: "inline-block",
            padding: "12px 14px",
            borderRadius: 14,
            maxWidth: "100%",
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
            wordBreak: "word-break",
            hyphens: "auto",
            background: isMe ? "#007BFF" : "#E0E0E0",
            color: isMe ? "#fff" : "#111",
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          }}
        >
          {text}
        </div>
      </div>

      {isMe && (
        <img
          src={avatar}
          alt=""
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
          }}
          onError={(e) => (e.currentTarget.src = "/default-avatar.png")}
        />
      )}
    </div>
  );
}

/* ───────────────────────── Page ───────────────────────── */
export default function RevealPartner() {
  const { eventId } = useParams(); // /reveal/:eventId/partner
  const navigate = useNavigate();
  const location = useLocation();
  const api = process.env.REACT_APP_API_URL;
  const { keyboardOffset } = useKeyboardDock();

  useEffect(() => {
    enforceRevealState({ api, eventId, pageKind: "partner", navigate });
  }, [api, eventId, navigate]);

  // Stage coming from navigation or server (for trio: 0 then 1)
  const lockedStageRaw =
    typeof location.state?.stage === "number" ? Number(location.state.stage) : null;

  // persist “entered this page in stage-locked mode”
  const cameLockedRef = useRef(lockedStageRaw != null);
  const lockedStage = lockedStageRaw;

  const [currentEpoch, setCurrentEpoch] = useState(null);

  // multi-match state (authoritative from /match or /state)
  const [matches, setMatches] = useState([]);
  const [visTotalStages, setVisTotalStages] = useState(1);

  // Server decides tab count (via header/uiStages). Never infer from lockedStage alone.
  useEffect(() => {
    // keep in sync with matches if server hasn't told us yet
    if (Array.isArray(matches) && matches.length === 2) setVisTotalStages(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches.length]);

  // Small helper to apply tab-count intent from server (header/uiStages)
  function coerceVisStages(val) {
    const n = Number(val);
    if (n === 2) setVisTotalStages(2);
    else if (n === 1) setVisTotalStages(1);
  }

  const [tabIdx, setTabIdx] = useState(0); // used when not locked

  // active match (computed below)
  const [active, setActive] = useState(null);

  const [booting, setBooting] = useState(true);

  // user + partner docs
  const [me, setMe] = useState(null);
  const [partnerDoc, setPartnerDoc] = useState(null);

  // per-room messages cache and the list bound to the active chat
  const [roomMsgs, setRoomMsgs] = useState({}); // { [roomId]: Message[] }
  const [msgs, setMsgs] = useState([]);
  const listRef = useRef(null);

  // header timer (pair-level only)
  const [roundEndAt, setRoundEndAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);

  // measured layout refs/heights (header + composer)
  const headerRef = useRef(null);
  const inputBarRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);
  const [inputH, setInputH] = useState(56);

  const socket = useMemo(() => getRevealSocket(), []);

  /* ───────── presence + access watchdogs ───────── */
  useEffect(() => {
    startLocationHeartbeat();
    startRevealAccessWatch(eventId, {
      onDenied: () => navigate(`/events/${eventId}`, { replace: true }),
      intervalMs: 15000,
    });
    return () => {
      stopRevealAccessWatch(eventId);
      stopLocationHeartbeat();
    };
  }, [eventId, navigate]);

  useEffect(() => {
    const s = getRevealSocket();
    const onDenied = () => navigate(`/events/${eventId}`, { replace: true });
    s.on("reveal:accessDenied", onDenied);
    return () => s.off("reveal:accessDenied", onDenied);
  }, [eventId, navigate]);

  // Load me
  useEffect(() => {
    const myId = getUserId();
    if (!myId) return;
    axios
      .get(`${api}/users/${myId}`, authHeaders())
      .then(({ data }) => setMe(data))
      .catch(() => {});
  }, [api]);

  // Fetch event to know currentEpoch
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${api}/events/${eventId}?t=${Date.now()}`,
          authHeaders()
        );
        setCurrentEpoch(Number(data?.revealEpoch ?? 0));
      } catch {}
    })();
  }, [api, eventId]);

  // Socket: hard epoch change → kick to portal immediately
  useEffect(() => {
    function onEpoch({ eventId: eid, epoch }) {
      if (String(eid) !== String(eventId)) return;
      setCurrentEpoch(epoch);
      navigate(`/reveal/${eventId}/portal`, { replace: true });
    }
    socket.on("reveal:epoch", onEpoch);
    return () => socket.off("reveal:epoch", onEpoch);
  }, [socket, navigate, eventId]);

  /* ───────── bind /match payload ───────── */
  function bindMatchResponse(data) {
    const arr = Array.isArray(data.matches)
      ? data.matches.map((m) => ({ ...m, matchId: String(m.matchId || "") }))
      : data.roomId && data.partnerId
      ? [
          {
            roomId: String(data.roomId),
            partnerId: String(data.partnerId),
            score: data.score ?? 0,
            partnerHasTwo: !!data.partnerHasTwo,
            matchId: String(data.matchId || ""),
          },
        ]
      : [];
    if (!arr.length) return false;

    // Extra diagnostics (non-breaking)
    try {
      console.log("[Partner] bindMatchResponse IN", {
        from: data?.state || "(none)",
        stageIndex: Number.isInteger(data?.stageIndex) ? data.stageIndex : null,
        uiStages: data?.uiStages || null,
        matches: (data?.matches || []).map((m) => ({
          matchId: String(m.matchId || ""),
          roomId: String(m.roomId || ""),
          verifyEndAt: m.verifyEndAt || null,
        })),
      });
    } catch {}

    setMatches(arr);

    console.log("[TOGGLE] bindMatchResponse incoming:", {
      matches: data.matches?.map((m) => ({
        matchId: m.matchId,
        verifyEndAt: m.verifyEndAt,
      })),
    });

    // ❌ Do not seed any global/event timer here; we only use per-pair verifyEndAt
    try {
      if (arr[0]?.roomId) socket.emit("join", { roomId: arr[0].roomId });
    } catch {}

    // After set to observe effective active/disabled
    try {
      setTimeout(() => {
        const idx =
          Number.isInteger(data?.stageIndex) ? data.stageIndex : (typeof lockedStage === "number" ? lockedStage : tabIdx);
        const am = arr[Math.max(0, Math.min(arr.length - 1, idx))] || null;
        console.log("[Partner] bindMatchResponse AFTER", {
          chosenIdx: idx,
          activeMatchId: am?.matchId || null,
          activeRoomId: am?.roomId || null,
          activeVerifyEndAt: am?.verifyEndAt || null,
          disabled: !am?.verifyEndAt || !am?.roomId,
        });
      }, 0);
    } catch {}

    return true;
  }

  /* ───────── helper: hydrate from /state when Stage-2 is open ───────── */
  const hydrateFromStateIfStage2Open = async () => {
    try {
      const st = await axios.get(
        `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
        authHeaders()
      );
      try {
        const sd0 = st?.data || {};
        console.log("[Partner] /state RX", {
          status: st?.status,
          route: sd0?.route,
          guard: sd0?.guard,
          stageIndex: sd0?.stageIndex,
          uiStages: sd0?.uiStages || null,
          matches: (sd0?.matches || []).map((m) => ({
            matchId: String(m.matchId || ""),
            roomId: String(m.roomId || ""),
            verifyEndAt: m.verifyEndAt || null,
          })),
        });
      } catch {}

      // Apply server hint for tab count from /state if present
      try { coerceVisStages(st?.headers?.["x-reveal-totalstages"]); } catch {}

      const sd = st?.data || {};
      if (
        st?.status === 200 &&
        sd?.guard === "stage2-open" &&
        Array.isArray(sd?.matches) &&
        sd.matches.length > 0
      ) {
        bindMatchResponse({ state: "match", matches: sd.matches });
        if (Number.isFinite(sd?.stageIndex)) setTabIdx(sd.stageIndex);
        if (sd?.uiStages && Number.isFinite(sd.uiStages.total))
          setVisTotalStages(sd.uiStages.total);

        try {
          setTimeout(() => {
            const idx =
              Number.isInteger(sd?.stageIndex) ? sd.stageIndex : (typeof lockedStage === "number" ? lockedStage : tabIdx);
            const arr = sd.matches || [];
            const am = arr[Math.max(0, Math.min(arr.length - 1, idx))] || null;
            console.log("[Partner] /state APPLY (S2-open)", {
              chosenIdx: idx,
              activeMatchId: am?.matchId || null,
              activeRoomId: am?.roomId || null,
              activeVerifyEndAt: am?.verifyEndAt || null,
              disabled: !am?.verifyEndAt || !am?.roomId,
            });
          }, 0);
        } catch {}

        return true;
      }
    } catch {}
    return false;
  };

  /* ───────── poll /match ───────── */
  useEffect(() => {
    if (currentEpoch == null) return;
    let stop = false;
    (async function loop() {
      if (stop) return;
      try {
        const res = await axios.get(`${api}/reveal-mode/${eventId}/match`, {
          ...authHeaders(),
          validateStatus: (s) => s === 200 || s === 202 || s === 204 || s === 410,
        });

        // Always apply server tab-count header if present
        try { coerceVisStages(res.headers?.["x-reveal-totalstages"]); } catch {}

        if (res.status === 200 && res.data?.state === "match") {
          try {
            console.log("[Partner] /match 200", {
              epoch: res.data?.epoch,
              uiStages: res.data?.uiStages || null,
              matches: (res.data?.matches || []).map((m) => ({
                matchId: String(m.matchId || ""),
                roomId: String(m.roomId || ""),
                verifyEndAt: m.verifyEndAt || null,
              })),
            });
          } catch {}

          if (
            currentEpoch != null &&
            typeof res.data.epoch === "number" &&
            res.data.epoch !== currentEpoch
          ) {
            // ignore mismatch; keep polling
          } else if (bindMatchResponse(res.data)) {
            if (res.data?.uiStages && Number.isFinite(res.data.uiStages.total)) {
              setVisTotalStages(Number(res.data.uiStages.total));
            } else if (Array.isArray(res.data?.matches) && res.data.matches.length >= 2) {
              setVisTotalStages(2);
            }
          }
        } else if (res.status === 204) {
          try {
            console.log("[Partner] /match 204 WAIT", {
              guard: res.headers?.["x-reveal-guard"] || null,
              route: res.headers?.["x-reveal-route"] || "partner",
            });
          } catch {}

          // 204 = “wait/locked/bridging”: hydrate visuals only; NEVER navigate on 204
          const ok = await hydrateFromStateIfStage2Open();
          if (!ok) {
            try {
              const st = await axios.get(
                `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
                authHeaders()
              );

              // Apply header from /state response if present
              try { coerceVisStages(st?.headers?.["x-reveal-totalstages"]); } catch {}

              const sd = st?.data || {};

              // Visual hints (no routing)
              if (sd?.uiStages && Number.isFinite(sd.uiStages.total)) {
                setVisTotalStages(Number(sd.uiStages.total));
              }
              if (Number.isInteger(sd?.stageIndex)) {
                setTabIdx(sd.stageIndex);
              }

              // Prefer a direct server placeholder (no extra fetch)
              if (sd.route === "partner" && Array.isArray(sd.matches) && sd.matches.length > 0) {
                bindMatchResponse({ state: "match", matches: sd.matches });
                if (Number.isInteger(sd?.stageIndex)) setTabIdx(sd.stageIndex);
                if (sd?.uiStages && Number.isFinite(sd.uiStages.total)) {
                  setVisTotalStages(Number(sd.uiStages.total));
                }
                return;
              }

              // Optional: placeholder from matchId
              if (sd.route === "partner" && sd.matchId) {
                try {
                  const mres = await axios.get(`${api}/matches/${sd.matchId}`, authHeaders());
                  const mdoc = mres?.data?.match || mres?.data || null;
                  if (mdoc) {
                    const meId = getUserId();
                    let partnerId = sd.partnerId || null;
                    if (!partnerId) {
                      const other =
                        (mdoc.participants || []).find(
                          (p) => String(p?._id || p) !== String(meId)
                        ) || null;
                      partnerId = other?._id || String(other) || "";
                    }
                    const placeholder = {
                      roomId: "",
                      partnerId,
                      partner: null,
                      score: 0,
                      partnerHasTwo: false,
                      matchId: String(mdoc._id),
                      verifyEndAt: null,
                    };
                    setMatches([placeholder]);
                    setTabIdx(0);
                    setActive(placeholder);
                  }
                } catch {}
              }
            } catch {}
          }
        } else if (res.status === 410 && res.data?.state === "ended") {
          try {
            console.log("[Partner] /match 410 ENDED", {
              hdrEpoch: res.headers?.["x-reveal-event-epoch"] || null,
            });
          } catch {}
          navigate(`/reveal/${eventId}/portal`, {
            replace: true,
            state: { revealExpired: true },
          });
          return;
        } else if (res.status === 410) {
          const ended = res.data?.state === "ended";
          const hdrGuard = String(res.headers?.["x-reveal-guard"] || "").toLowerCase();
          const hdrEpoch = Number(res.headers?.["x-reveal-event-epoch"]);
          const sameEpoch = Number.isFinite(hdrEpoch) ? hdrEpoch === currentEpoch : true;
          if (ended && sameEpoch && !hdrGuard) {
            navigate(`/reveal/${eventId}/portal`, {
              replace: true,
              state: { revealExpired: true },
            });
            return;
          }
        }
      } catch (e) {
        const s = e?.response?.status;
        if (s === 410) {
          try {
            console.log("[Partner] /match 410 ENDED (catch)");
          } catch {}
          navigate(`/reveal/${eventId}/portal`, {
            replace: true,
            state: { revealExpired: true },
          });
          return;
        }
      }
      setTimeout(loop, 3000);
    })();
    return () => {
      stop = true;
    };
  }, [api, eventId, currentEpoch, navigate, socket]);

  /* ───────── header countdown (pair-level only) ───────── */
  useEffect(() => {
    if (!roundEndAt) return;

    const id = setInterval(async () => {
      const left = roundEndAt - Date.now();
      setTimeLeft(left);

      if (left <= 0) {
        if (!active?.verifyEndAt) return;
        try {
          const { data } = await axios.get(
            `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
            authHeaders()
          );
          const want = String(data?.route || "portal");
          if (want === "partner") return;

          navigate(`/reveal/${eventId}/${want}`, {
            replace: true,
            state: {
              revealExpired: true,
              stage: Number.isFinite(data?.stageIndex) ? data.stageIndex : null,
              matchId: data?.matchId || null,
              stageEndsAt: data?.endMs || null,
            },
          });
        } catch {
          navigate(`/reveal/${eventId}/portal`, {
            replace: true,
            state: { revealExpired: true },
          });
        }
      }
    }, 1000);

    return () => clearInterval(id);
  }, [roundEndAt, navigate, eventId, api, active?.verifyEndAt]);

  const isTrio = matches.length === 2;
  const anyStageUnlocked = matches.some((m) => !!m.verifyEndAt);
  const effectiveLockedStage =
    anyStageUnlocked ? null : typeof lockedStage === "number" ? lockedStage : null;

  /* ───────── active match selection ───────── */
  useEffect(() => {
    if (!matches.length) return;

    const localAnyStageUnlocked = matches.some((m) => !!m.verifyEndAt);
    const localEffectiveLock =
      localAnyStageUnlocked ? null : typeof lockedStage === "number" ? lockedStage : null;

    const idx =
      localEffectiveLock != null
        ? Math.max(0, Math.min(matches.length - 1, localEffectiveLock))
        : Math.max(0, Math.min(matches.length - 1, tabIdx));

    setActive(matches[idx]);

    // Diagnostics after React updates settle
    try {
      setTimeout(() => {
        const am = matches[Math.max(0, Math.min(matches.length - 1, idx))] || null;
        console.log("[Partner] ACTIVE CHANGED", {
          stage: idx,
          activeMatchId: am?.matchId || null,
          activeRoomId: am?.roomId || null,
          activeVerifyEndAt: am?.verifyEndAt || null,
          disabled: !am?.verifyEndAt || !am?.roomId,
        });
      }, 0);
    } catch {}
  }, [matches, tabIdx, lockedStage]);

  // RevealPartner.jsx
  // Replace your current "isLive / tick" effect with this one:
  useEffect(() => {
    // Consider "live" as soon as the server exposes a verify window
    const isLive = !!active?.verifyEndAt;
    if (isLive) return;

    // Allow the server a short settle window right after landing
    const SETTLE_MS = 6000; // tweak 4–8s as needed
    const landedAt = Date.now();
    let stop = false;

    const tick = async () => {
      if (stop) return;

      try {
        const res = await axios.get(
          `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
          { ...authHeaders(), validateStatus: (s) => [200, 202, 204, 410].includes(s) }
        );

        // 204 = “waiting/hand-off”. Do nothing; keep user on Partner.
        if (res.status === 204) {
          // apply any header hint even on 204
          try { coerceVisStages(res.headers?.["x-reveal-totalstages"]); } catch {}
          setTimeout(tick, 1200);
          return;
        }

        const data   = res.data || {};
        const route  = String(data.route || 'portal');
        const guard  = String(data.guard || '').toLowerCase();
        const hdrG   = String(res.headers?.['x-reveal-guard'] || '').toLowerCase();
        const total  = Number.isFinite(data?.uiStages?.total) ? Number(data.uiStages.total) : 1;
        const age    = Date.now() - landedAt;

        // Apply header hint for tab count
        try { coerceVisStages(res.headers?.["x-reveal-totalstages"]); } catch {}

        // If server is affirmatively Partner or indicates a partner-guard, stay.
        const partnerish =
          route === 'partner' ||
          guard === 'stage2-locked' ||
          guard === 'stage2-open' ||
          hdrG === 'stage2-locked';

        if (res.status === 200) {
          if (partnerish) {
            // Still in a partner phase; keep waiting for verifyEndAt/room to show
            setTimeout(tick, 1200);
            return;
          }

          // Only kick single-stage users off Partner when the server explicitly routes away
          // *and* we’ve given a short settle window after reveal.
          if (total <= 1 && route !== 'partner' && age > SETTLE_MS) {
            setMatches([]);
            try {
              sessionStorage.removeItem(`reveal_partner_sticky_until:${eventId}`);
              sessionStorage.removeItem(`reveal_inmatch_until:${eventId}`);
              sessionStorage.removeItem(`reveal_block_return:${eventId}`);
              sessionStorage.setItem(`reveal_route_lock:${eventId}`, String(Date.now() + 1200));
            } catch {}
            navigate(`/reveal/${eventId}/portal`, { replace: true });
            return;
          }

          // Otherwise don't kick; keep checking
          setTimeout(tick, 1200);
          return;
        }

        if (res.status === 410) {
          // Ended: if we *still* don't have a live verify, go to Portal.
          setMatches([]);
          try {
            sessionStorage.removeItem(`reveal_partner_sticky_until:${eventId}`);
            sessionStorage.removeItem(`reveal_inmatch_until:${eventId}`);
            sessionStorage.removeItem(`reveal_block_return:${eventId}`);
            sessionStorage.setItem(`reveal_route_lock:${eventId}`, String(Date.now() + 1200));
          } catch {}
          navigate(`/reveal/${eventId}/portal`, { replace: true, state: { revealExpired: true } });
          return;
        }

        // 202 or anything else: keep waiting
        setTimeout(tick, 1200);
      } catch {
        // Network hiccup: keep waiting instead of kicking
        setTimeout(tick, 1200);
      }
    };

    tick();
    return () => { stop = true; };
  }, [api, eventId, navigate, active?.verifyEndAt]);

  /* ───────── seed pair-timer only from verifyEndAt ───────── */
  useEffect(() => {
    if (!active?.verifyEndAt) return;
    const endMs = new Date(active.verifyEndAt).getTime();
    if (!Number.isFinite(endMs)) return;
    setRoundEndAt(endMs);
  }, [active?.verifyEndAt]);

  /* ───────── partner snapshot ───────── */
  useEffect(() => {
    if (!active?.partnerId) return;
    if (active?.partner) {
      setPartnerDoc(active.partner);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${api}/users/${active.partnerId}`, authHeaders());
        setPartnerDoc(data || null);
      } catch {
        navigate(`/reveal/${eventId}/portal`, { replace: true });
      }
    })();
  }, [api, active?.partnerId, navigate, eventId]);

  /* ───────── messages for active room ───────── */
  useEffect(() => {
    if (!active?.roomId) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${api}/chat/rooms/${active.roomId}/messages`,
          authHeaders()
        );
        const normalized = (data || []).map((m) => ({
          ...m,
          text: m.text ?? m.content,
        }));
        setRoomMsgs((prev) => ({ ...prev, [active.roomId]: normalized }));
        setMsgs(normalized);
        requestAnimationFrame(() => {
          const el = listRef.current;
          if (el?.scrollTo) el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
        });
      } catch (e) {
        const status = e?.response?.status;
        navigate(`/reveal/${eventId}/portal`, {
          replace: true,
          state: { revealExpired: status === 410 },
        });
      }
    })();
  }, [api, active?.roomId, navigate, eventId]);

  useEffect(() => {
    if (!active?.roomId) return;
    setMsgs(roomMsgs[active.roomId] || []);
  }, [active?.roomId, roomMsgs]);

  useEffect(() => {
    function onNewMessage(m) {
      if (!m?.roomId) return;
      setRoomMsgs((prev) => {
        const list = prev[m.roomId] || [];
        return { ...prev, [m.roomId]: [...list, { ...m, text: m.text ?? m.content }] };
      });
      if (m.roomId === active?.roomId) {
        setMsgs((prev) => [...prev, { ...m, text: m.text ?? m.content }]);
      }
    }
    socket.on("newMessage", onNewMessage);
    return () => socket.off("newMessage", onNewMessage);
  }, [socket, active?.roomId]);

  useEffect(() => {
    requestAnimationFrame(() => {
      const el = listRef.current;
      if (el?.scrollTo) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }, [msgs]);

  useEffect(() => {
    if (!active?.roomId) return;
    requestAnimationFrame(() => {
      const el = listRef.current;
      if (el?.scrollTo) el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
    });
  }, [active?.roomId]);

  /* ───────── reconnect handling ───────── */
  useEffect(() => {
    async function onReconnect() {
      console.log("[Partner] socket reconnect — attempting hydration");
      // On reconnect, try to hydrate from /state (instant Stage-2 unlock) before /match
      const ok = await hydrateFromStateIfStage2Open();
      if (ok) {
        console.log("[Partner] reconnect hydration via /state S2-open -> ok");
        return;
      }

      if (!matches.length) {
        try {
          const res = await axios.get(`${api}/reveal-mode/${eventId}/match`, {
            ...authHeaders(),
            validateStatus: (s) => s === 200 || s === 202 || s === 204 || s === 410,
          });
          // Apply header if present on reconnect
          try { coerceVisStages(res.headers?.["x-reveal-totalstages"]); } catch {}

          if (res.status === 200 && res.data?.state === "match") {
            bindMatchResponse(res.data);
            console.log("[Partner] reconnect hydration via /match 200 -> bound");
            return;
          }
          if (res.status === 410) {
            try {
              const st = await axios.get(
                `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
                authHeaders()
              );
              if (st?.data?.route === "partner") {
                return;
              }
            } catch {}
            navigate(`/reveal/${eventId}/portal`, {
              replace: true,
              state: { revealExpired: true },
            });
            return;
          }
        } catch (e) {
          const s = e?.response?.status;
          if (s === 410) {
            navigate(`/reveal/${eventId}/portal`, {
              replace: true,
              state: { revealExpired: true },
            });
          }
        }
      } else if (active?.roomId) {
        try {
          socket.emit("join", { roomId: active.roomId });
        } catch {}
      }
    }
    socket.on("connect", onReconnect);
    return () => socket.off("connect", onReconnect);
  }, [socket, api, eventId, matches.length, active?.roomId, navigate]);

  /* ───────── stage-unlocked (R2 handoff) ───────── */
  useEffect(() => {
    if (!socket) return;
  
    async function onStageUnlocked(payload) {
      console.log("[Partner] socket unlock RX", payload);
      console.log("[Partner] BEFORE unlock snapshot", {
        stage: typeof lockedStage === "number" ? lockedStage : tabIdx,
        activeMatchId: active?.matchId || null,
        activeRoomId: active?.roomId || null,
        activeVerify: active?.verifyEndAt || null,
        disabled: !active?.verifyEndAt || !active?.roomId,
      });
  
      const { nextMatchId, verifyEndAt, stageIndex, roomId } = payload || {};
  
      // 🔹 1) Merge verifyEndAt + roomId into the correct match
      try {
        if (nextMatchId && (verifyEndAt || roomId)) {
          setMatches((prev) =>
            (prev || []).map((m) =>
              String(m.matchId) === String(nextMatchId)
                ? {
                    ...m,
                    verifyEndAt: verifyEndAt || m.verifyEndAt || null,
                    roomId: roomId || m.roomId || "",
                  }
                : m
            )
          );
        }
  
        // 🔹 2) Update active immediately if it’s this match
        setActive((prev) => {
          if (!prev) return prev;
          if (String(prev.matchId) !== String(nextMatchId)) return prev;
          const updated = {
            ...prev,
            verifyEndAt: verifyEndAt || prev?.verifyEndAt || null,
            roomId: roomId || prev?.roomId || "",
          };
          return updated;
        });
  
        // 🔹 3) Apply stage index if provided
        if (Number.isInteger(stageIndex)) {
          setTabIdx(stageIndex);
        }
  
        // 🔹 4) Join chat room + prefetch messages
        if (roomId) {
          try {
            socket.emit("join", { roomId });
          } catch {}
          try {
            const { data } = await axios.get(
              `${api}/chat/rooms/${roomId}/messages`,
              authHeaders()
            );
            const normalized = (data || []).map((m) => ({
              ...m,
              text: m.text ?? m.content,
            }));
            setRoomMsgs((prev) => ({ ...prev, [roomId]: normalized }));
            setMsgs(normalized);
          } catch (e) {
            console.warn("[Partner] prefetch messages failed", e?.response?.status || e?.message);
          }
        }
      } catch (e) {
        console.warn("[Client] optimistic unlock merge failed", e);
      }
  
      // 🔹 5) Confirm authoritative state after merge
      const ok = await hydrateFromStateIfStage2Open();
      if (!ok) {
        try {
          enforceRevealState({ api, eventId, pageKind: "partner", navigate });
        } catch {}
      }
  
      // 🔹 6) Log post-unlock snapshot
      setTimeout(() => {
        const idx = typeof lockedStage === "number" ? lockedStage : tabIdx;
        const am =
          (matches || [])[Math.max(0, Math.min((matches?.length || 1) - 1, idx))] ||
          active ||
          null;
        console.log("[Partner] AFTER unlock snapshot", {
          stage: idx,
          activeMatchId: am?.matchId || null,
          activeRoomId: am?.roomId || null,
          activeVerify: am?.verifyEndAt || null,
          disabled: !am?.verifyEndAt || !am?.roomId,
        });
      }, 0);
    }
  
    socket.on("reveal:stage-unlocked", onStageUnlocked);
    return () => socket.off("reveal:stage-unlocked", onStageUnlocked);
  }, [socket, api, eventId, navigate, active, matches, tabIdx, lockedStage]);
  
  useEffect(() => {
    if (!socket) return;
    function onRoute(payload) {
      try {
        sessionStorage.removeItem(`reveal_route_lock:${eventId}`);
        sessionStorage.removeItem(`reveal_partner_sticky_until:${eventId}`);
        sessionStorage.removeItem(`reveal_inmatch_until:${eventId}`);
      } catch {}

      if (payload?.guard === "stage2-locked" || payload?.stageIndex === 1) {
        setVisTotalStages(2);
      }
      if (Number.isInteger(payload?.stageIndex)) {
        setTabIdx(payload.stageIndex);
      }

      window.dispatchEvent(new CustomEvent("reveal:route-intent", { detail: payload }));

      if (payload?.route) {
        navigate(`/reveal/${eventId}/${payload.route}`, {
          replace: true,
          state: { stage: payload.stageIndex ?? null, fromSocket: true },
        });
      }
    }
    socket.on("reveal:route", onRoute);
    return () => socket.off("reveal:route", onRoute);
  }, [socket, eventId, navigate]);

  useEffect(() => {
    function onRouteIntent(e) {
      const p = e.detail || {};
      if (!p?.route) return;
      navigate(`/reveal/${eventId}/${p.route}`, {
        replace: true,
        state: { stage: p.stageIndex ?? null, fromSocket: true },
      });
    }
    window.addEventListener("reveal:route-intent", onRouteIntent);
    return () => window.removeEventListener("reveal:route-intent", onRouteIntent);
  }, [eventId, navigate]);

  // Visibility/focus wake-up → hydrate from /state first (avoid spinner)
  useEffect(() => {
    const recheck = async () => {
      const ok = await hydrateFromStateIfStage2Open();
      if (!ok) {
        // do nothing; /match poll loop is already running
      }
    };
    const onVisible = () => document.visibilityState === "visible" && recheck();
    const onFocus = () => recheck();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ───────── switch tabs (disabled when locked) ───────── */
  function switchTab(i) {
    if (typeof lockedStage === "number") return;
    if (i === tabIdx) return;
    setTabIdx(i);
    const m = matches[i];
    if (m?.roomId) {
      try {
        socket.emit("join", { roomId: m.roomId });
      } catch {}
      setMsgs(roomMsgs[m.roomId] || []);
    }
  }

  // ── focus helpers to fix iOS tap-after-send issue
  const chatDockRef = useRef(null);
  const setDockRefs = (el) => {
    inputBarRef.current = el;
    chatDockRef.current = el;
  };
  function focusDockInput() {
    try {
      const host = chatDockRef.current;
      if (!host) return;
      const el =
        host.querySelector('textarea, input[type="text"], input[type="search"], input:not([type])') ||
        host.querySelector('[contenteditable="true"]');
      if (el && typeof el.focus === "function") {
        el.focus();
        if ("selectionStart" in el) {
          const len = el.value?.length ?? 0;
          el.setSelectionRange(len, len);
        }
      }
    } catch {}
  }
  function focusInnerComposer(e) {
    try {
      const t = e?.target;
      if (t && (t.closest?.('[role="button"]') || t.closest?.("button"))) return;
      e?.preventDefault?.();
      e?.stopPropagation?.();
      // microtask helps iOS refocus right after blur/send
      setTimeout(focusDockInput, 0);
    } catch {}
  }

  async function sendMessage(text) {
    if (!text || !active?.roomId) return;
    try {
      await axios.post(
        `${api}/chat/rooms/${active.roomId}/messages`,
        { text },
        authHeaders()
      );
      // Re-focus the composer so user can keep typing without another tap
      focusDockInput();
    } catch (e) {
      const status = e?.response?.status;
      navigate(`/reveal/${eventId}/portal`, {
        replace: true,
        state: { revealExpired: status === 410 },
      });
    }
  }

  /* ───────── layout measurements ───────── */
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setHeaderH(Math.max(0, Math.ceil(r.height)));
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    const imgs = el.querySelectorAll("img");
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", measure, { once: true });
    });
    return () => {
      ro.disconnect();
      imgs.forEach(
        (img) => img.removeEventListener && img.removeEventListener("load", measure)
      );
    };
  }, [partnerDoc, matches.length, timeLeft]);

  useEffect(() => {
    if (!inputBarRef.current) return;
    const measure = () => {
      const r = inputBarRef.current.getBoundingClientRect();
      setInputH(Math.max(0, Math.ceil(r.height)));
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(inputBarRef.current);
    return () => ro.disconnect();
  }, []);

  /* ───────── preload hydration ───────── */
  useEffect(() => {
    let hydrated = false;

    const navState = (window.history.state && window.history.state.usr) || null;
    if (navState && navState.state === "match" && navState.roomId) {
      bindMatchResponse(navState);
      hydrated = true;
    }

    if (!hydrated) {
      try {
        const raw = sessionStorage.getItem("reveal_preload_match");
        if (raw) {
          const preload = JSON.parse(raw);
          if (preload && preload.state === "match") {
            bindMatchResponse(preload);
            hydrated = true;
          }
        }
      } catch {}
    }

    try {
      sessionStorage.removeItem("reveal_preload_match");
    } catch {}
    setBooting(false);
  }, []);

  // ───────── navigate to verification ─────────
  const openCodeVerification = async () => {
    // If this stage is still locked (no verifyEndAt), do nothing.
    const waiting = !active?.verifyEndAt;
    if (waiting) return;

    const mid = active?.matchId || matches[tabIdx]?.matchId || null;

    if (mid) {
      try {
        await markFoundPartner(api, mid);
      } catch (_) {}
    }

    navigate(`/reveal/${eventId}/verify`, {
      replace: false,
      state: {
        stage: typeof lockedStage === "number" ? lockedStage : null,
        matchId: mid,
      },
    });
  };

  /* ───────── empty state ───────── */
  if (!matches.length) {
    if (booting) {
      return (
        <div style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
          <div style={{ height: 1 }} />
        </div>
      );
    }
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E7E9EE",
            borderRadius: 10,
            padding: "12px 14px",
            color: "#6b7280",
            fontWeight: 600,
          }}
        >
          Matching you now… hang tight.
        </div>
      </div>
    );
  }

  const topOffset = headerH || 200;

  // Pair is "waiting" until this pair exposes verifyEndAt
  // Pair is "waiting" until this pair exposes verifyEndAt
  const isWaitingForThisStage = !active?.verifyEndAt;

  // ── Toggle visibility rules ───────────────────────────────────
  // We only render the Partner1/Partner2 toggle when ALL are true:
  // 1) Server says there are 2 stages (header/uiStages) AND
  // 2) We have an explicit match with partnerHasTwo === false
  //    (meaning *I* am the trio main; my partners don't have two)
  // This avoids showing the toggle for the single/waiting user,
  // even if a header briefly says "2" or partnerHasTwo is missing.
  const serverSaysTwo = visTotalStages === 2;

  // NOTE: if /state came first, partnerHasTwo might be undefined.
  // Treat "undefined" as unknown; require an explicit `false` to show the toggle.
  const iAmTrioMain = Array.isArray(matches) && matches.some(m => m?.partnerHasTwo === false);

  // Final switch: only show for the trio main user.
  const showTwoTabs = serverSaysTwo && iAmTrioMain;

  const stageLocked = effectiveLockedStage !== null && matches.length === 2;
  return (
    <div style={P.page}>
      {/* Navbar + header */}
      <div ref={headerRef}>
        <RevealPortalNavBar eventId={eventId} />

        <RevealChatHeader
          partnerName={partnerDoc ? formatName(partnerDoc) : "Your partner"}
          partnerAvatar={avatarUrl(partnerDoc)}
          matchCount={showTwoTabs ? 2 : 1}
          numMatches={showTwoTabs ? 2 : matches.length}
          hasSwitch={showTwoTabs}
          onSwitchPartner={showTwoTabs ? switchTab : undefined}
          activeIndex={effectiveLockedStage !== null ? effectiveLockedStage : tabIdx}
          subheading={
            isWaitingForThisStage
              ? (active?.partnerHasTwo
                  ? "Your match has another reveal right now. You’ll be able to chat in < 15 minutes."
                  : "Please wait — your round will open soon.")
              : "Say hello and coordinate where to meet 👋"
          }
          waiting={isWaitingForThisStage}
          suppressTimer={isWaitingForThisStage}
          timeLeftMs={Math.max(0, timeLeft)}
          /* NEW: render but disable the Found Partner button while waiting */
          foundDisabled={!active?.verifyEndAt}
          foundDisabledReason={
            !active?.verifyEndAt
              ? (active?.partnerHasTwo
                  ? "Opens after their other reveal finishes."
                  : "Opens when this round starts.")
              : undefined
          }
          onFoundPartner={openCodeVerification}
        />
      </div>

      {/* SCROLLABLE AREA */}
      <div
        ref={listRef}
        style={{
          position: "fixed",
          top: topOffset,
          left: 0,
          right: 0,
          // Use CSS var from hook to hug keyboard & safe-area
          bottom: `calc(${inputH}px + max(env(safe-area-inset-bottom, 0px), var(--kb-offset, 0px)))`,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          background: "#fff",
          padding: "12px 0 10px",
        }}
      >
        <div style={{ maxWidth: 720, width: "94%", margin: "0 auto" }}>
          {isWaitingForThisStage ? (
            <div style={P.waitWrap}>
              <div style={P.spinner} />
              <div style={P.waitText}>
                Waiting for your match to finish their other reveal. You’ll be able to chat
                shortly.
              </div>
            </div>
          ) : active?.roomId ? (
            msgs.length ? (
              msgs.map((m) => {
                const meSide = String(m.senderId) === String(getUserId());
                const name = meSide
                  ? null
                  : formatName(
                      String(partnerDoc?._id) === String(m.senderId) ? partnerDoc : me
                    );
                const avatar = meSide ? avatarUrl(me) : avatarUrl(partnerDoc);

                return (
                  <MessageRow
                    key={m.messageId || `${m.senderId}-${m.createdAt}`}
                    isMe={meSide}
                    avatar={avatar}
                    name={name}
                    text={m.text}
                  />
                );
              })
            ) : (
              <div style={P.empty}>Say hi to kick things off.</div>
            )
          ) : (
            <div style={P.pending}>
              Waiting for everyone to accept… your chat will appear here.
            </div>
          )}
          <div id="__bottom_sentinel" />
        </div>
      </div>

      {/* COMPOSER */}
      <div
        ref={setDockRefs}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          // Hug keyboard & safe-area using CSS var
          bottom: "max(env(safe-area-inset-bottom, 0px), var(--kb-offset, 0px))",
          zIndex: 1000,                // ensure on top
          background: "#fff",
          boxShadow: "0 -1px 0 rgba(0,0,0,0.06)",
          pointerEvents: "auto",
        }}
        onTouchStartCapture={focusInnerComposer}
        onMouseDownCapture={focusInnerComposer}
      >
        {(() => {
          // composer decision trace
          try {
            console.log("[Partner] RENDER COMPOSER", {
              waiting: !active?.verifyEndAt,
              hasRoom: !!active?.roomId,
              disabled: !active?.verifyEndAt || !active?.roomId,
              stage: typeof lockedStage === "number" ? lockedStage : tabIdx,
              activeMatchId: active?.matchId || null,
            });
          } catch {}
          return null;
        })()}

        <ChatInput
          onSend={sendMessage}
          placeholder={
            isWaitingForThisStage
              ? "Chat unlocks once your match is ready"
              : active?.roomId
              ? "Send message…"
              : "Chat unlocks when everyone accepts"
          }
          disabled={isWaitingForThisStage || !active?.roomId}
          style={{
            position: "static",
            padding: "10px 12px calc(10px + env(safe-area-inset-bottom, 0px))",
          }}
          debugLabel={(() => {
            console.log("[TOGGLE] ChatInput render", {
              stage: typeof lockedStage === "number" ? lockedStage : tabIdx,
              activeMatchId: active?.matchId,
              verifyEndAt: active?.verifyEndAt || null,
              disabled: isWaitingForThisStage || !active?.roomId,
              waiting: isWaitingForThisStage,
              hasRoom: !!active?.roomId,
            });
            return "";
          })()}
        />
      </div>
    </div>
  );
}

/* ───────────────────────── Styles ───────────────────────── */
const P = {
  page: {
    // Fixed shell prevents whole-page reflow on mobile when keyboard/URL bar animates
    position: "fixed",
    inset: 0,
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    height: "100dvh",
    overflow: "hidden",
    background: "#fff",
    // Older fallbacks:
    minHeight: "100svh",
    minHeightFallback: "-webkit-fill-available",
  },
  
  empty: {
    padding: 12,
    textAlign: "center",
    color: "#6b7280",
    fontWeight: 700,
    marginTop: -12,
  },
  pending: {
    padding: "12px 14px",
    textAlign: "center",
    color: "#6B7280",
    fontWeight: 600,
    background: "#FFFFFF",
    border: "1px solid #E7E9EE",
    borderRadius: 10,
    lineHeight: 1.35,
    letterSpacing: "-0.01em",
  },
  waitWrap: {
    display: "grid",
    placeItems: "center",
    gap: 10,
    padding: "16px 14px",
    textAlign: "center",
    color: "#6B7280",
    background: "#FFFFFF",
    border: "1px solid #E7E9EE",
    borderRadius: 10,
  },
  spinner: {
    width: 22,
    height: 22,
    border: "2px solid #e5e7eb",
    borderTopColor: "#0b0b0b",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  waitText: {
    fontWeight: 600,
    lineHeight: 1.35,
    letterSpacing: "-0.01em",
  },
};

/* global spinner keyframes */
const style = document.createElement("style");
style.innerHTML = `
@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }

/* Calm scrolling while keyboard is open (iOS) */
body.kbd-open {
  overscroll-behavior-y: contain;
  touch-action: manipulation;
}
`;
document.head.appendChild(style);
